// One realtime WebSocket (ADR 0013), shared by the SharedWorker and the per-tab fallback. The server only says
// "workspace W is at seq S" / "your inbox changed"; callers pull through the normal sync protocol.
export type RtServerMsg = { t: 'ws'; w: string; s: number } | { t: 'inbox' } | { t: 'subd'; w: string[]; s: Record<string, number> } | { t: 'auth' } | { t: 'pong' };
/** connecting → open; "unavailable" after repeated failures without ever opening (proxy/browser without WebSocket). */
export type RtStatus = 'connecting' | 'open' | 'unavailable' | 'closed';

export class RealtimeSocket {
  private ws: WebSocket | null = null;
  private subs: string[] = [];
  private fails = 0;
  private opened = false;
  private stopped = false;
  private retryT: ReturnType<typeof setTimeout> | null = null;
  private pingT: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly url: string,
    private readonly onMessage: (m: RtServerMsg) => void,
    private readonly onStatus: (s: RtStatus) => void,
  ) {}

  start(): void { this.stopped = false; this.open(); }
  stop(): void {
    this.stopped = true;
    if (this.retryT) clearTimeout(this.retryT);
    if (this.pingT) clearInterval(this.pingT);
    this.ws?.close(1000);
    this.ws = null;
    this.onStatus('closed');
  }
  /** Reconnect now (e.g. another account signed in: the handshake carries the new session cookie). */
  restart(): void { this.ws?.close(1000); this.ws = null; this.fails = 0; this.opened = false; if (this.retryT) clearTimeout(this.retryT); this.open(); }
  subscribe(workspaces: string[]): void {
    this.subs = [...new Set(workspaces)].sort();
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: 'sub', w: this.subs }));
  }

  private open(): void {
    if (this.stopped) return;
    this.onStatus('connecting');
    let ws: WebSocket;
    try { ws = new WebSocket(this.url); } catch { this.failed(); return; }
    this.ws = ws;
    ws.onopen = () => {
      this.fails = 0; this.opened = true;
      this.onStatus('open');
      ws.send(JSON.stringify({ t: 'sub', w: this.subs }));
      if (this.pingT) clearInterval(this.pingT);
      this.pingT = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('{"t":"ping"}'); }, 25_000);
    };
    ws.onmessage = e => { try { this.onMessage(JSON.parse(String(e.data)) as RtServerMsg); } catch { /* ignore malformed */ } };
    ws.onclose = () => { if (this.ws === ws) { this.ws = null; if (this.pingT) clearInterval(this.pingT); this.failed(); } };
  }

  private failed(): void {
    if (this.stopped) return;
    this.fails++;
    // Never opened after 3 tries: report "unavailable" (callers fall back to long-polling) but keep retrying slowly.
    if (!this.opened && this.fails >= 3) this.onStatus('unavailable'); else this.onStatus('connecting');
    const wait = Math.min(60_000, 1000 * 2 ** Math.min(this.fails, 6)) * (0.5 + Math.random() / 2);
    this.retryT = setTimeout(() => this.open(), wait);
  }
}

export const realtimeUrl = (origin: string): string => origin.replace(/^http/, 'ws') + '/rt/ws';
